import React from "react";
import Cropper from "react-easy-crop";
import { X, Check, ZoomIn, ZoomOut } from "lucide-react";
import { getCroppedImage } from "../../utils/cropImage";

/**
 * Props:
 *   cropper    – object from useImageCropper()
 *   onSave     – (blob) => void
 *   title      – string shown in header  (default "Crop Image")
 *   aspect     – number  e.g. 1 for square, 16/9 for landscape  (default 1)
 *   cropShape  – "rect" | "round"  (default "rect")
 */
const ImageCropModal = ({
  cropper,
  onSave,
  title = "Crop Image",
  aspect = 1,
  cropShape = "rect",
}) => {
  if (!cropper.open) return null;

  const handleSave = async () => {
    const blob = await getCroppedImage(
      cropper.imageSrc,
      cropper.croppedAreaPixels,
      cropShape === "round"
    );
    onSave(blob);
    cropper.closeCropper();
  };

  return (
    <div
      className="icc-backdrop"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) cropper.closeCropper();
      }}
    >
      <div className="icc-modal">
        {/* ── Header ── */}
        <div className="icc-header">
          <button
            type="button"
            className="icc-btn-close"
            onClick={cropper.closeCropper}
            title="Cancel"
          >
            <X size={18} />
          </button>

          <span className="icc-title">{title}</span>

          <button
            type="button"
            className="icc-btn-done"
            onClick={handleSave}
            title="Apply crop"
          >
            <Check size={15} />
            <span>Done</span>
          </button>
        </div>

        {/* ── Crop canvas ── */}
        <div className="icc-canvas">
          <Cropper
            image={cropper.imageSrc}
            crop={cropper.crop}
            zoom={cropper.zoom}
            aspect={aspect}
            cropShape={cropShape}
            showGrid
            onCropChange={cropper.setCrop}
            onZoomChange={cropper.setZoom}
            onCropComplete={cropper.onCropComplete}
            style={{
              containerStyle: { borderRadius: 0 },
            }}
          />

          {/* Hint */}
          <div className="icc-hint">Pinch or use slider to zoom · Drag to reposition</div>
        </div>

        {/* ── Zoom footer ── */}
        <div className="icc-footer">
          <button
            type="button"
            className="icc-zoom-btn"
            onClick={() => cropper.setZoom((z) => Math.max(z - 0.1, 1))}
          >
            <ZoomOut size={18} />
          </button>

          <div className="icc-slider-wrap">
            <input
              type="range"
              className="icc-slider"
              min={1}
              max={3}
              step={0.01}
              value={cropper.zoom}
              onChange={(e) => cropper.setZoom(Number(e.target.value))}
            />
          </div>

          <button
            type="button"
            className="icc-zoom-btn"
            onClick={() => cropper.setZoom((z) => Math.min(z + 0.1, 3))}
          >
            <ZoomIn size={18} />
          </button>

          <span className="icc-zoom-pct">{Math.round(cropper.zoom * 100)}%</span>
        </div>
      </div>

      <style>{`
        /* ───────────────────────────────
           BACKDROP
        ─────────────────────────────── */
        .icc-backdrop {
          position: fixed;
          inset: 0;
          background: rgba(0, 0, 0, 0.75);
          backdrop-filter: blur(4px);
          -webkit-backdrop-filter: blur(4px);
          z-index: 99999;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 16px;
          animation: icc-fade-in 0.2s ease;
        }

        @keyframes icc-fade-in {
          from { opacity: 0; }
          to   { opacity: 1; }
        }

        /* ───────────────────────────────
           MODAL CARD
        ─────────────────────────────── */
        .icc-modal {
          width: 100%;
          max-width: 420px;
          background: #18181b;
          border-radius: 20px;
          overflow: hidden;
          display: flex;
          flex-direction: column;
          box-shadow: 0 32px 64px rgba(0, 0, 0, 0.6), 0 0 0 1px rgba(255,255,255,0.06);
          animation: icc-slide-up 0.25s cubic-bezier(0.34, 1.56, 0.64, 1);
        }

        @keyframes icc-slide-up {
          from { opacity: 0; transform: translateY(24px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0)   scale(1);    }
        }

        /* ───────────────────────────────
           HEADER
        ─────────────────────────────── */
        .icc-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 14px 16px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.08);
        }

        .icc-title {
          font-size: 0.9rem;
          font-weight: 600;
          color: #f4f4f5;
          letter-spacing: 0.2px;
        }

        .icc-btn-close {
          width: 34px;
          height: 34px;
          border-radius: 50%;
          border: none;
          background: rgba(255, 255, 255, 0.08);
          color: #a1a1aa;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          transition: background 0.2s, color 0.2s;
        }

        .icc-btn-close:hover {
          background: rgba(239, 68, 68, 0.2);
          color: #ef4444;
        }

        .icc-btn-done {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 7px 16px;
          border-radius: 10px;
          border: none;
          background: linear-gradient(135deg, #6052a5, #4b3d9a);
          color: #ffffff;
          font-size: 0.82rem;
          font-weight: 600;
          cursor: pointer;
          transition: opacity 0.2s, transform 0.15s;
          box-shadow: 0 4px 12px rgba(96, 82, 165, 0.4);
        }

        .icc-btn-done:hover {
          opacity: 0.9;
          transform: translateY(-1px);
        }

        .icc-btn-done:active {
          transform: translateY(0);
        }

        /* ───────────────────────────────
           CROP CANVAS
        ─────────────────────────────── */
        .icc-canvas {
          position: relative;
          width: 100%;
          height: 340px;
          background: #09090b;
        }

        .icc-hint {
          position: absolute;
          bottom: 10px;
          left: 50%;
          transform: translateX(-50%);
          background: rgba(0, 0, 0, 0.55);
          color: rgba(255, 255, 255, 0.65);
          font-size: 0.68rem;
          padding: 4px 12px;
          border-radius: 20px;
          white-space: nowrap;
          pointer-events: none;
          backdrop-filter: blur(4px);
        }

        /* ───────────────────────────────
           ZOOM FOOTER
        ─────────────────────────────── */
        .icc-footer {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 14px 18px;
          border-top: 1px solid rgba(255, 255, 255, 0.08);
          background: #1c1c1f;
        }

        .icc-zoom-btn {
          width: 34px;
          height: 34px;
          border-radius: 50%;
          border: none;
          background: rgba(255, 255, 255, 0.08);
          color: #d4d4d8;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          flex-shrink: 0;
          transition: background 0.2s;
        }

        .icc-zoom-btn:hover {
          background: rgba(255, 255, 255, 0.15);
          color: #fff;
        }

        .icc-slider-wrap {
          flex: 1;
          display: flex;
          align-items: center;
        }

        .icc-slider {
          width: 100%;
          height: 4px;
          -webkit-appearance: none;
          appearance: none;
          background: rgba(255, 255, 255, 0.15);
          border-radius: 99px;
          outline: none;
          cursor: pointer;
          accent-color: #7c6fd0;
        }

        .icc-slider::-webkit-slider-thumb {
          -webkit-appearance: none;
          width: 18px;
          height: 18px;
          border-radius: 50%;
          background: #7c6fd0;
          box-shadow: 0 0 0 3px rgba(124, 111, 208, 0.25);
          cursor: grab;
          transition: box-shadow 0.2s;
        }

        .icc-slider::-webkit-slider-thumb:active {
          cursor: grabbing;
          box-shadow: 0 0 0 5px rgba(124, 111, 208, 0.35);
        }

        .icc-slider::-moz-range-thumb {
          width: 18px;
          height: 18px;
          border: none;
          border-radius: 50%;
          background: #7c6fd0;
          cursor: grab;
        }

        .icc-zoom-pct {
          font-size: 0.72rem;
          color: #71717a;
          min-width: 36px;
          text-align: right;
          font-variant-numeric: tabular-nums;
        }

        /* ───────────────────────────────
           MOBILE
        ─────────────────────────────── */
        @media (max-width: 480px) {
          .icc-modal {
            border-radius: 16px;
          }
          .icc-canvas {
            height: 280px;
          }
          .icc-hint {
            font-size: 0.62rem;
          }
        }
      `}</style>
    </div>
  );
};

export default ImageCropModal;
